import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';
import merge from 'lodash/merge';
import config from 'config';
import type { EntitlementErrorPayload } from '../../store/upgradeDialog';

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

interface QueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

// Transient-failure retry policy. Tunables here are deliberately tiny —
// aggressive retries pile load on an already-struggling backend during
// incidents. Cap retries, cap delay, and only retry verbs the server is
// allowed to replay.
const MAX_TRANSIENT_RETRIES = 2;
const BASE_BACKOFF_MS = 250;
const MAX_BACKOFF_MS = 5_000;
const IDEMPOTENT_METHODS = new Set(['get', 'head', 'options']);

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseRetryAfter(headerValue: string | undefined): number | null {
  if (!headerValue) return null;
  const seconds = Number(headerValue);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1000, MAX_BACKOFF_MS);
  const dateMs = Date.parse(headerValue);
  if (!Number.isNaN(dateMs)) return Math.max(0, Math.min(dateMs - Date.now(), MAX_BACKOFF_MS));
  return null;
}

function isRetryableStatus(status: number | undefined): boolean {
  if (!status) return false;
  if (status === 429) return true;
  if (status >= 500 && status < 600 && status !== 501 && status !== 505) return true;
  return false;
}

class Http {
  private instance: AxiosInstance | null = null;
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];

  private defaults: AxiosRequestConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
    },
  };

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.init();
  }

  private processQueue(error: Error | null, token: string | null = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  private clearAuth() {
    // Just clear the tokens - let React handle the redirect via context
    // This avoids hard page reloads that can cause issues
    localStorage.removeItem(config.api.access_token_key);
    localStorage.removeItem(config.api.refresh_token_key);
  }

  private async attemptTokenRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem(config.api.refresh_token_key);

    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${config.api.base_url}/api/auth/refresh`, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem(config.api.access_token_key, accessToken);
      if (newRefreshToken) {
        localStorage.setItem(config.api.refresh_token_key, newRefreshToken);
      }

      return accessToken;
    } catch {
      return null;
    }
  }

  public init({
    config: axiosConfig,
    configFn,
  }: {
    config?: AxiosRequestConfig;
    configFn?: (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>;
  } = {}) {
    const http = axios.create(merge(this.defaults, axiosConfig));

    // Request interceptor - add auth token
    http.interceptors.request.use(
      (reqConfig: InternalAxiosRequestConfig): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
        // When sending FormData (file uploads), the Content-Type MUST be unset so Axios
        // can set it automatically with the correct multipart boundary.
        // This must happen after all merging because Axios instance defaults and configFn
        // both bake in 'application/json' which survives lodash/merge.
        const stripContentTypeForFormData = (cfg: InternalAxiosRequestConfig) => {
          if (cfg.data instanceof FormData && cfg.headers) {
            delete (cfg.headers as any)['Content-Type'];
          }
          return cfg;
        };

        if (configFn) {
          const requestConfig = configFn(reqConfig);

          if (requestConfig instanceof Promise) {
            return requestConfig.then((newConfig) =>
              stripContentTypeForFormData(merge(reqConfig, newConfig) as InternalAxiosRequestConfig)
            );
          }

          return stripContentTypeForFormData(merge(reqConfig, requestConfig || {}) as InternalAxiosRequestConfig);
        }

        return stripContentTypeForFormData(reqConfig);
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors and token refresh
    http.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const status = error.response?.status;

        // Handle 401 - attempt token refresh
        if (status === StatusCode.Unauthorized && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Token refresh already in progress, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                if (originalRequest.headers) {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                }
                return http(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.attemptTokenRefresh();

            if (newToken) {
              // Refresh successful - retry original request and queued requests
              this.processQueue(null, newToken);

              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }

              return http(originalRequest);
            } else {
              // Refresh failed - clear auth, let React handle redirect
              this.processQueue(new Error('Token refresh failed'), null);
              this.clearAuth();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            this.processQueue(refreshError as Error, null);
            this.clearAuth();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Retry transient failures (429 + retryable 5xx) on idempotent verbs.
        // POST/PUT/PATCH/DELETE are NEVER retried automatically — some are
        // side-effecting (create payment, send invite), and replaying them
        // can double-charge or double-send. Callers that know their POST is
        // safe to replay can opt in via originalRequest.headers['x-retry-ok'].
        const method = (originalRequest.method || 'get').toLowerCase();
        const optedIn = originalRequest.headers?.['x-retry-ok'] === 'true';
        const replayable = IDEMPOTENT_METHODS.has(method) || optedIn;
        const retryCount = (originalRequest as { _retryCount?: number })._retryCount ?? 0;

        if (replayable && retryCount < MAX_TRANSIENT_RETRIES && isRetryableStatus(status)) {
          const retryAfterHeader = error.response?.headers?.['retry-after'] as string | undefined;
          const retryAfter = parseRetryAfter(retryAfterHeader);
          // Honor the server's Retry-After when present (429), otherwise
          // exponential backoff: 250ms, 750ms, 2250ms… capped at 5s.
          const backoff = retryAfter ?? Math.min(BASE_BACKOFF_MS * 3 ** retryCount, MAX_BACKOFF_MS);
          (originalRequest as { _retryCount?: number })._retryCount = retryCount + 1;
          await sleep(backoff);
          return http(originalRequest);
        }

        // Handle other errors
        return this.handleError(error);
      }
    );

    this.instance = http;
    return http;
  }

  public get request(): AxiosInstance {
    return this.http;
  }

  private handleError(error: AxiosError) {
    const status = error.response?.status;

    switch (status) {
      case StatusCode.InternalServerError:
        // Handle InternalServerError
        console.error('Internal Server Error:', error.message);
        break;
      case StatusCode.Forbidden: {
        // Entitlement 403s (limit reached / feature not in plan / expired plan)
        // are surfaced globally as the "Upgrade plan" dialog. We dispatch to the
        // Zustand store outside React, then STILL re-reject below so any local
        // catch handlers keep working unchanged. The store is dynamically
        // imported to keep this widely-imported leaf module init-order safe.
        const data = error.response?.data as EntitlementErrorPayload | undefined;
        if (data?.code === 'USAGE_LIMIT_EXCEEDED' || data?.code === 'FEATURE_NOT_ENTITLED') {
          void import('../../store/upgradeDialog').then(({ useUpgradeDialogStore }) =>
            useUpgradeDialogStore.getState().open(data),
          );
        }
        break;
      }
      case StatusCode.TooManyRequests:
        // Handle TooManyRequests
        console.error('Too Many Requests:', error.message);
        break;
    }

    return Promise.reject(error);
  }
}

const httpInstance = new Http();
export default httpInstance;
